import { TableCellProps } from "@mui/material";
import { createColumnHelper, flexRender } from "@tanstack/react-table";
import { useMemo } from "react";
import { ActionNames } from "tng-address-collection-shared/actionNames.ts";
import { fullName } from "tng-address-collection-shared/fullName.ts";
import { CardsListItem } from "tng-address-collection-shared/types/apiTypes.ts";
import { statusSortRank } from "../columns/cardStatuses.ts";
import { sortSigned } from "../columns/sortSigned.ts";
import { MemoDetailButton } from "../components/DetailButton.tsx";
import { MemoDetailClickCell } from "../components/DetailClickCell.tsx";
import { MailToCell } from "../components/MailToCell.tsx";
import { SignatureCheckbox } from "../components/SignatureCheckbox.tsx";
import { StatusCell } from "../components/StatusCell.tsx";
import { useTranslator } from "../i18n/translator.ts";
import { useUserHasAction } from "./useUserHasAction.ts";

export interface Meta {
  align?: TableCellProps["align"];
}

const userFriendlyDateTime = (dateTimeIso: string | undefined) =>
  dateTimeIso?.replace("T", "\u00a0").replace(/:\d{2}\.\d{3}Z/, "");

export const useSignatureListColumns = (
  handleDetailClick: (row: CardsListItem) => void,
  projectId: string,
  hiddenFields: string[],
  duplicatedNames: ReadonlySet<string>,
) => {
  const translator = useTranslator();
  const columnHelper = useMemo(() => createColumnHelper<CardsListItem>(), []);
  const userHasAction = useUserHasAction(projectId);

  return useMemo(() => {
    const salutationMap: Record<CardsListItem["salutation"], string> = {
      male: translator("male"),
      female: translator("female"),
    };
    const formalityMap: Record<CardsListItem["formality"], string> = {
      formal: translator("formal"),
      informal: translator("informal"),
      "semi-formal": translator("semiFormal"),
    };

    return [
      {
        column: columnHelper.accessor(
          (row) => statusSortRank(row, hiddenFields, duplicatedNames),
          {
            id: "status",
            sortDescFirst: false,
            header: () => translator("statusColumn"),
            cell: (info) =>
              flexRender(StatusCell, {
                card: info.row.original,
                hiddenFields,
                duplicatedNames,
              }),
            meta: {} satisfies Meta,
          },
        ),
      },
      {
        hide:
          hiddenFields.includes("isSignedByUser") &&
          hiddenFields.includes("numberOfSignatures") &&
          hiddenFields.includes("signatures"),
        column: columnHelper.accessor(
          ({ numberOfSignatures, signatures }) => ({
            numberOfSignatures,
            signatures,
          }),
          {
            id: "signature",
            header: () => translator("cardTableHeaderSigned"),
            sortingFn: (rowA, rowB) =>
              sortSigned(
                {
                  signed: rowA.getIsSelected(),
                  numberOfSignatures: rowA.original.numberOfSignatures,
                },
                {
                  signed: rowB.getIsSelected(),
                  numberOfSignatures: rowB.original.numberOfSignatures,
                },
              ),
            cell: (info) => {
              const isSelected = info.row.getIsSelected();
              const { numberOfSignatures, signatures } = info.getValue();
              return flexRender(SignatureCheckbox, {
                cardId: info.row.original.id,
                cardName: fullName(info.row.original),
                projectId,
                isChecked: isSelected,
                numberOfSignatures,
                signatures,
                onChanged: (newState) => info.row.toggleSelected(newState),
                canEditSignatures: userHasAction(
                  ActionNames.editCardSignatures,
                ),
                canRemoveOtherSignatures: !info.row.original.shared,
              });
            },
            meta: { align: "center" } satisfies Meta,
          },
        ),
      },
      {
        hide: hiddenFields.includes("title"),
        column: columnHelper.accessor("title", {
          id: "title",
          header: () => translator("cardTableHeaderTitle"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
              pointer: !row.original.shared,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: false,
        column: columnHelper.accessor(fullName, {
          id: "name",
          header: () => translator("cardTableHeaderName"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
              pointer: !row.original.shared,
            }),
          size: 350,
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("email"),
        column: columnHelper.accessor((row) => row.email, {
          id: "email",
          header: () => translator("cardTableHeaderEmail"),
          cell: ({ getValue }) => flexRender(MailToCell, { email: getValue() }),
          size: 350,
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("language"),
        column: columnHelper.accessor("language", {
          id: "language",
          header: () => translator("cardTableHeaderLanguage"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
              pointer: !row.original.shared,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("salutation"),
        column: columnHelper.accessor("salutation", {
          id: "salutation",
          header: () => translator("cardTableHeaderSalutation"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: salutationMap[getValue()],
              userHasAction,
              pointer: !row.original.shared,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("formality"),
        column: columnHelper.accessor("formality", {
          id: "formality",
          header: () => translator("cardTableHeaderFormality"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: formalityMap[getValue()],
              userHasAction,
              pointer: !row.original.shared,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("receivesGift"),
        column: columnHelper.accessor(
          (row) =>
            row.receivesGift
              ? translator("cardTableCellYes")
              : translator("cardTableCellNo"),
          {
            id: "receivesGift",
            header: () => translator("cardTableHeaderReceivesGift"),
            cell: ({ row, getValue }) =>
              flexRender(MemoDetailClickCell, {
                handleClick: () => handleDetailClick(row.original),
                value: getValue(),
                userHasAction,
                pointer: !row.original.shared,
              }),
            meta: {} satisfies Meta,
          },
        ),
      },
      {
        hide: hiddenFields.includes("companyName"),
        column: columnHelper.accessor("companyName", {
          id: "companyName",
          header: () => translator("cardTableHeaderCompanyName"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
              pointer: !row.original.shared,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("departmentName"),
        column: columnHelper.accessor("departmentName", {
          id: "departmentName",
          header: () => translator("cardTableHeaderDepartmentName"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
              pointer: !row.original.shared,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("senderDisplayName"),
        column: columnHelper.accessor("senderDisplayName", {
          id: "sender",
          header: () => translator("cardTableHeaderSender"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
              pointer: !row.original.shared,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("manualDispatch"),
        column: columnHelper.accessor(
          (row) =>
            row.manualDispatch
              ? translator("cardTableCellDispatchManual")
              : translator("cardTableCellDispatchAutomatic"),
          {
            id: "manualDispatch",
            header: () => translator("cardTableHeaderDispatch"),
            cell: ({ row, getValue }) =>
              flexRender(MemoDetailClickCell, {
                handleClick: () => handleDetailClick(row.original),
                value: getValue(),
                userHasAction,
                pointer: !row.original.shared,
              }),
            meta: {} satisfies Meta,
          },
        ),
      },
      {
        hide: hiddenFields.includes("comment"),
        column: columnHelper.accessor("comment", {
          id: "comment",
          header: () => translator("cardTableHeaderComment"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
              pointer: !row.original.shared,
            }),
          // High value to ensure the other columns remain small
          size: 2_000,
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("created"),
        column: columnHelper.accessor("created.at", {
          id: "created",
          header: () => translator("cardTableHeaderCreatedAt"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: userFriendlyDateTime(getValue()),
              userHasAction,
              pointer: !row.original.shared,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: hiddenFields.includes("lastChanged"),
        column: columnHelper.accessor("lastChanged.at", {
          id: "lastChanged",
          header: () => translator("cardTableHeaderLastChangedAt"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: userFriendlyDateTime(getValue()),
              userHasAction,
              pointer: !row.original.shared,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: !userHasAction(ActionNames.editCard),
        column: columnHelper.display({
          id: "edit",
          header: "",
          cell: (info) =>
            info.row.original.shared
              ? null
              : flexRender(MemoDetailButton, {
                  onDetailClick: () => handleDetailClick(info.row.original),
                }),
          size: 70,
          meta: { align: "center" } satisfies Meta,
        }),
      },
    ]
      .filter((x) => !x.hide)
      .map((x) => x.column);
  }, [
    translator,
    hiddenFields,
    columnHelper,
    userHasAction,
    projectId,
    handleDetailClick,
    duplicatedNames,
  ]);
};
